import { Box } from '@mentimeter/ragnar-ui/box';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import type { ScalesVisualizationTypeRenderOptions } from '@mentimeter/visualizations-react/types/render-options-by-slide-type/scales';
import type { PropsWithChildren } from 'react';
import { NonInteractiveVisualizationWrapper } from '../../shared/components/NonInteractiveVisualizationWrapper';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { type UseScalesPresentationReturn } from './hooks/use-scales-presentation';
import { ScreenReaderResults } from './ScreenReaderResults/ScreenReaderResults';
import { ScalesVisualizationDataHandler } from './visualizations/ScalesVisualizationDataHandler';
import { SpiderVisualization } from './visualizations/SpiderVisualization';
import { transformScalesForVisualization } from './utils/transformResultsVisualization';

export type ScalesPresentationProps = UseScalesPresentationReturn & {
  visualizationTypeRenderOptions?: ScalesVisualizationTypeRenderOptions;
};

export const ScalesPresentation = ({
  title,
  styledTitle,
  label,
  longerDescription,
  fontSizeOffset,
  visualizationType,
  choices,
  range,
  dimensionLabels,
  results,
  shouldHideResults,
  shouldShowOverallAverage,
  shouldAnimate,
  CanvasVisualizationArea,
  visualizationTypeRenderOptions,
  children,
}: PropsWithChildren<ScalesPresentationProps>) => {
  const visualizationResults = shouldHideResults
    ? []
    : transformScalesForVisualization(results, choices);

  const ConditionalWrapper =
    CanvasVisualizationArea ?? NonInteractiveVisualizationWrapper;

  return (
    <Box
      width="100%"
      height="100%"
      data-testid="scales-container"
      alignItems="center"
      justifyContent="center"
    >
      <TitleSection
        title={title}
        styledTitle={styledTitle}
        description={longerDescription}
        label={label}
        fontSizeOffset={fontSizeOffset}
        showTextSizeDropdown={true}
      />
      {children || (
        <ConditionalWrapper flex="1" width="100%">
          <>
            {visualizationType === 'slider-chart' ? (
              <ScalesVisualizationDataHandler
                results={visualizationResults}
                range={range}
                choices={choices}
                dimensionLabels={dimensionLabels}
                shouldShowOverallAverage={shouldShowOverallAverage}
                shouldHideResults={shouldHideResults}
                shouldAnimate={shouldAnimate}
                {...visualizationTypeRenderOptions?.sliderChart}
              />
            ) : (
              <SpiderVisualization
                choices={choices}
                results={visualizationResults}
                range={range}
                shouldAnimate={shouldAnimate}
                {...visualizationTypeRenderOptions?.spiderChart}
              />
            )}
            <ScreenReaderOnly>
              <ScreenReaderResults
                results={visualizationResults}
                range={range}
                dimensionLabels={dimensionLabels}
                shouldHideResults={shouldHideResults}
              />
            </ScreenReaderOnly>
          </>
        </ConditionalWrapper>
      )}
    </Box>
  );
};
